// components/common/ErrorBoundary/ErrorFallback.tsx
/**
 * ErrorFallback - Styled fallback UI for error boundaries
 */
import { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { GlassCard, CardContent } from '../Card';
import { Button } from '../Button';

export type ErrorSeverity = 'page' | 'section' | 'widget';

export interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  severity?: ErrorSeverity;
  title?: string;
  message?: string;
}

/**
 * ErrorFallback - Displays a user-friendly error message
 *
 * Severity levels:
 * - 'page': Full page error (shows home button)
 * - 'section': Major section error (shows retry button)
 * - 'widget': Small widget error (minimal UI)
 */
export function ErrorFallback({
  error,
  resetError,
  severity = 'section',
  title,
  message,
}: ErrorFallbackProps): ReactElement {
  const handleRefresh = (): void => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = (): void => {
    window.location.href = '/';
  };

  // Widget-level: Minimal inline error
  if (severity === 'widget') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          backgroundColor: 'rgba(255, 107, 53, 0.1)',
          borderRadius: 1,
          border: '1px solid rgba(255, 107, 53, 0.3)',
        }}
      >
        <ErrorIcon sx={{ color: '#FF6B35', fontSize: 20 }} />
        <Typography sx={{ color: '#FF6B35', fontSize: '0.85rem' }}>
          {message || 'Failed to load'}
        </Typography>
        {resetError && (
          <Box
            component="span"
            onClick={resetError}
            sx={{
              color: '#00FFD4',
              fontSize: '0.85rem',
              cursor: 'pointer',
              ml: 'auto',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Retry
          </Box>
        )}
      </Box>
    );
  }

  // Section-level: Card-based error
  if (severity === 'section') {
    return (
      <GlassCard
        sx={{
          border: '1px solid rgba(255, 107, 53, 0.3)',
          backgroundColor: 'rgba(255, 107, 53, 0.05)',
        }}
      >
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <ErrorIcon sx={{ fontSize: 48, color: '#FF6B35', mb: 2 }} />
            <Typography
              variant="h6"
              sx={{
                color: '#FFF',
                fontWeight: 600,
                fontFamily: '"Rajdhani", sans-serif',
                mb: 1,
              }}
            >
              {title || 'Something went wrong'}
            </Typography>
            <Typography sx={{ color: '#8892A0', mb: 3, maxWidth: 400, mx: 'auto' }}>
              {message || 'This section encountered an error. Try refreshing or come back later.'}
            </Typography>
            {process.env.NODE_ENV === 'development' && error && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 1,
                  textAlign: 'left',
                  maxHeight: 150,
                  overflow: 'auto',
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    color: '#FF6B35',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </Typography>
              </Box>
            )}
            <Button
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Box>
        </CardContent>
      </GlassCard>
    );
  }

  // Page-level: Full page error with home button
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        backgroundColor: '#0A0A0F',
      }}
    >
      <GlassCard
        sx={{
          maxWidth: 500,
          width: '100%',
          border: '1px solid rgba(255, 107, 53, 0.3)',
        }}
      >
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 107, 53, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorIcon sx={{ fontSize: 40, color: '#FF6B35' }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: '#FFF',
                fontWeight: 700,
                fontFamily: '"Rajdhani", sans-serif',
                mb: 1,
              }}
            >
              {title || 'Oops! Something went wrong'}
            </Typography>
            <Typography sx={{ color: '#8892A0', mb: 4 }}>
              {message || "We're sorry, but something unexpected happened. Please try again or return to the home page."}
            </Typography>
            {process.env.NODE_ENV === 'development' && error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 1,
                  textAlign: 'left',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    color: '#FF6B35',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
                variant="outlined"
              >
                Try Again
              </Button>
              <Button
                onClick={handleGoHome}
                startIcon={<HomeIcon />}
                color="primary"
              >
                Go Home
              </Button>
            </Box>
          </Box>
        </CardContent>
      </GlassCard>
    </Box>
  );
}

export default ErrorFallback;
